import { Collapse } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import {
  InfoBlock,
  InfoBlockSections,
} from 'app/components/commons/InfoBlock/InfoBlock';
import type {
  BookingDetails,
  BookingRefundResponse,
} from 'app/typings/core/admin/bookings/booking';
import { dateAndHour } from 'app/utils/dates';
import { capitalizeFirstLetter } from 'app/utils/string';

const { Panel } = Collapse;

type RefundsProps = {
  bookingDetails: BookingDetails;
};

export const Refunds = ({ bookingDetails }: RefundsProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const formatRefundName = (
    refund: BookingRefundResponse,
    totalRefund: number
  ) => {
    return `Refund - ${dateAndHour(refund.createdAt)} - ${currencyFormatter(
      totalRefund
    )}`;
  };

  return (
    <InfoBlock title="Refunds">
      <Collapse>
        {bookingDetails.refunds.map((refund) => {
          const totalRefund =
            (refund.creditAmount ?? 0) +
            (refund.giftCardAmount ?? 0) +
            (refund.creditCardAmount ?? 0);

          const header = formatRefundName(refund, totalRefund);

          return (
            <Panel header={header} key={header}>
              <InfoBlockSections
                sections={[
                  {
                    rows: [
                      {
                        label: 'Total Refund',
                        value: currencyFormatter(totalRefund),
                        subRows: [
                          {
                            label: 'Credit Card Amount',
                            value: currencyFormatter(
                              refund.creditCardAmount ?? 0
                            ),
                          },
                          {
                            label: 'Gift Card Amount',
                            value: currencyFormatter(
                              refund.giftCardAmount ?? 0
                            ),
                          },
                          {
                            label: 'Credit Amount',
                            value: currencyFormatter(refund.creditAmount ?? 0),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    rows: [
                      {
                        label: 'Amount due by hotel',
                        value: currencyFormatter(
                          refund.paymentRefunds[0].amountDueByHotel ?? 0
                        ),
                      },
                      {
                        label: 'Amount due by Staycation',
                        value: currencyFormatter(
                          totalRefund -
                            (refund.paymentRefunds[0].amountDueByHotel ?? 0)
                        ),
                      },
                    ],
                  },
                  {
                    rows: [
                      {
                        label: 'Paid by',
                        value: capitalizeFirstLetter(refund.source),
                      },
                    ],
                  },
                  {
                    rows: [
                      {
                        label: 'Created at',
                        value: dateAndHour(refund.createdAt),
                      },
                      {
                        label: 'Created by',
                        value: `${refund.user?.firstName ?? ''} ${
                          refund.user?.lastName ?? ''
                        }`,
                      },
                      {
                        label: 'Reason type',
                        value: refund.reason ?? '-',
                      },
                      {
                        label: 'Comment',
                        value: refund.comment?.trim() ? refund.comment : '-',
                      },
                    ],
                  },
                ]}
              />
            </Panel>
          );
        })}
      </Collapse>
    </InfoBlock>
  );
};
